import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import { BoardTrend, BoardRing } from './shared/BoardCharts';
import './TableroEconomia.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtS = (v) => `S/ ${Number(v||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtFecha = (s) => { if(!s) return '—'; const d=new Date(s); return isNaN(d)?s:d.toLocaleDateString('es-PE',{day:'2-digit',month:'2-digit',year:'numeric'}); };
const diasRestantes = (fechaFin) => {
  if(!fechaFin) return null;
  const fin=new Date(fechaFin), hoy=new Date(); hoy.setHours(0,0,0,0); fin.setHours(0,0,0,0);
  return Math.ceil((fin-hoy)/(1000*60*60*24));
};

const MOD_COLOR = {
  'INAFECTOS - CAJA': { color:'#65A991', label:'Caja', icon:'caja' },
  'AFECTOS - SISMED': { color:'#CEA16C', label:'SISMED', icon:'sismed' },
  'AFECTOS - DATAFARMA': { color:'#9990C7', label:'DataFarma', icon:'datafarma' },
};
const MOD_ORDER = ['INAFECTOS - CAJA','AFECTOS - SISMED','AFECTOS - DATAFARMA'];

// ── Número animado ──────────────────────────────────────────────────────────
const AnimatedNumber = ({ value, decimals=0, prefix='', suffix='', duration=900 }) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches){setDisplay(Number(value)||0);prevRef.current=Number(value)||0;return;}
    const start = prevRef.current;
    const end = Number(value)||0;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now-t0)/duration);
      const eased = 1 - Math.pow(1-p, 3);
      setDisplay(start + (end-start)*eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prevRef.current = end;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]); // eslint-disable-line
  return <>{prefix}{display.toLocaleString('es-PE',{minimumFractionDigits:decimals,maximumFractionDigits:decimals})}{suffix}</>;
};

// ── Skeleton pulse ───────────────────────────────────────────────────────────
const shimmerSx = {
  '@keyframes tableroShimmer': { '0%,100%':{opacity:0.35}, '50%':{opacity:0.75} },
  animation:'tableroShimmer 1.4s ease-in-out infinite',
};

// ── Card de módulo ───────────────────────────────────────────────────────────
const ModuloCard = ({ modulo, data, loading, esMesActual }) => {
  const cfg = MOD_COLOR[modulo] || { color:IOS.blue, label:modulo };
  const stats = [
    ['Aperturas cerr.', loading?null:(data?.aperturasCerradasMes ?? (esMesActual?'En curso':'—'))],
    ['Comprobantes', loading?null:(data?.cantidadComprobantes ?? '—')],
    ['Anulaciones', loading?null:`${data?.totalAnulaciones||0} · ${fmtS(data?.montoAnulaciones)}`],
    ['Notas Créd.', loading?null:`${data?.totalNotasCredito||0} · ${fmtS(data?.montoNotasCredito)}`],
  ];
  return (
    <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:2.5, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}` }}>
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
        <Box sx={{ width:32, height:32, borderRadius:'10px', bgcolor:`${cfg.color}12`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:cfg.color }} />
        </Box>
        <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>{cfg.label}</Typography>
      </Box>

      {loading ? (
        <Box sx={{ ...shimmerSx, height:34, width:'70%', borderRadius:'8px', bgcolor:IOS.bg, mb:1.5 }} />
      ) : (
        <Typography sx={{ fontSize:'1.7rem', fontWeight:800, color:'#374151', lineHeight:1, letterSpacing:'-0.02em', mb:1.5 }}>
          {data ? <AnimatedNumber value={data.totalRecaudadoMes||0} decimals={2} prefix="S/ " /> : '—'}
        </Typography>
      )}

      <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1 }}>
        {stats.map(([lbl,val],i)=>(
          <Box key={i} sx={{ bgcolor:IOS.bg, borderRadius:'10px', p:1 }}>
            <Typography sx={{ fontSize:'0.56rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.05em', mb:0.3 }}>{lbl}</Typography>
            {loading
              ? <Box sx={{ ...shimmerSx, height:12, width:'60%', borderRadius:'4px', bgcolor:IOS.separator }} />
              : <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:'#4B5563', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{val}</Typography>
            }
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Card secundaria genérica (Certificado / SUNAT / NC) ──────────────────────
const SecCard = ({ children }) => (
  <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:1.8, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, height:'100%' }}>
    {children}
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
const TableroEconomia = ({ modulo='economia' }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const nc = user?.nombreCompleto || user?.usuario || '';
  const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  

  const periodosLista = () => {
    const list = []; const now = new Date();
    for (let i = 0; i < 12; i++) {
      const f = new Date(now.getFullYear(), now.getMonth() - i, 1);
      list.push({ periodo: f.getFullYear(), mes: f.getMonth() + 1, label: f.toLocaleDateString('es-PE', { month:'short', year:'2-digit' }) });
    }
    return list;
  };
  const [periodos] = useState(periodosLista());
  const [periodoIdx, setPeriodoIdx] = useState(0);
  const periodoActual = periodos[periodoIdx];
  const esMesActual = periodoIdx === 0;

  const [kpi, setKpi] = useState([]);
  const [loadKpi, setLoadKpi] = useState(true);
  const [comprobantes, setComprobantes] = useState([]);
  const [loadComp, setLoadComp] = useState(true);
  const [tendencia, setTendencia] = useState([]);
  const [loadTend, setLoadTend] = useState(true);
  const [rangoDias, setRangoDias] = useState(30);
  const [modulosVisibles, setModulosVisibles] = useState(new Set(MOD_ORDER));
  const [tipoChart, setTipoChart] = useState('area'); // area | barras

  const [cert, setCert] = useState(null);
  const [pendientes, setPend] = useState([]);
  const [horaNC, setHoraNC] = useState(null);
  const [logNC, setLogNC] = useState([]);
  const [cargSec, setCargSec] = useState(true);

  const [ultimaAct, setUltimaAct] = useState(null);
  const [errors,setErrors]=useState({});
  const [secondaryRefresh,setSecondaryRefresh]=useState(0);
  const requests=useRef({kpi:0,tendencia:0,comprobantes:0});
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const cargarKpi = useCallback(async () => {
    const request=++requests.current.kpi;setErrors(e=>({...e,kpi:null}));setKpi([]);
    setLoadKpi(true);
    try {
      const r = await api.get('/economia/EconomiaTablero/kpi', { params:{ ueId, periodo:periodoActual.periodo, mes:periodoActual.mes } });
      if(request!==requests.current.kpi)return;
      if(!r.data.success)throw new Error('Consulta no disponible');
      if (r.data.success) setKpi(r.data.data);
      setUltimaAct(new Date());
    } catch(e){ if(request===requests.current.kpi)setErrors(old=>({...old,kpi:'No se pudo cargar kpi.'})); } finally { if(request===requests.current.kpi)setLoadKpi(false); }
  }, [ueId, periodoActual.periodo, periodoActual.mes]);

  const cargarTendencia = useCallback(async () => {
    const request=++requests.current.tendencia;setErrors(e=>({...e,tendencia:null}));setTendencia([]);
    setLoadTend(true);
    try {
      const r = await api.get('/economia/EconomiaTablero/tendencia', { params:{ ueId, dias:rangoDias } });
      if(request!==requests.current.tendencia)return;
      if(!r.data.success)throw new Error('Consulta no disponible');
      if (r.data.success) setTendencia(r.data.data);
    } catch(e){ if(request===requests.current.tendencia)setErrors(old=>({...old,tendencia:'No se pudo cargar tendencia.'})); } finally { if(request===requests.current.tendencia)setLoadTend(false); }
  }, [ueId, rangoDias]);

  const cargarComprobantes = useCallback(async () => {
    const request=++requests.current.comprobantes;setErrors(e=>({...e,comprobantes:null}));setComprobantes([]);
    setLoadComp(true);
    try {
      const r = await api.get('/economia/EconomiaTablero/comprobantes', { params:{ ueId, periodo:periodoActual.periodo, mes:periodoActual.mes } });
      if(request!==requests.current.comprobantes)return;
      if(!r.data.success)throw new Error('Consulta no disponible');
      if (r.data.success) setComprobantes(r.data.data);
    } catch(e){ if(request===requests.current.comprobantes)setErrors(old=>({...old,comprobantes:'No se pudo cargar comprobantes.'})); } finally { if(request===requests.current.comprobantes)setLoadComp(false); }
  }, [ueId, periodoActual.periodo, periodoActual.mes]);

  useEffect(()=>{ cargarKpi(); }, [cargarKpi]);
  useEffect(()=>{ cargarTendencia(); }, [cargarTendencia]);
  useEffect(()=>{ cargarComprobantes(); }, [cargarComprobantes]);

  useEffect(() => {
    let live=true;setCargSec(true);setErrors(e=>({...e,servicios:null}));
    Promise.all([
      api.get('/economia/configuracion/certificado',{params:{ueId}}).catch(()=>null),
      api.get('/economia/sunat/pendientes',{params:{ueId}}).catch(()=>null),
      api.get('/economia/configuracion/nota-credito',{params:{ueId}}).catch(()=>null),
      api.get('/economia/notas-credito/log',{params:{ueId}}).catch(()=>null),
    ]).then(([r1,r2,r3,r4])=>{
      if(!live)return;
      if([r1,r2,r3,r4].some(r=>!r?.data?.success))setErrors(e=>({...e,servicios:'No se pudo verificar toda la información de certificado, SUNAT o notas de crédito. No interpretes los valores como un estado confirmado.'}));
      if(r1?.data?.success && r1.data.data?.length) setCert(r1.data.data[0]);
      if(r2?.data?.success) setPend(r2.data.data||[]);
      if(r3?.data?.success && r3.data.data?.length) setHoraNC(r3.data.data[0]?.horA_ENVIO||null);
      if(r4?.data?.success) setLogNC(r4.data.data||[]);
    }).finally(()=>{if(live)setCargSec(false);});
    return ()=>{live=false;};
  }, [ueId,secondaryRefresh]);

  // ── Totales consolidados ────────────────────────────────────────────────
  const totMes = useMemo(()=>kpi.filter(k=>k.modulo!=='TOTAL CONSOLIDADO').reduce((s,k)=>s+Number(k.totalRecaudadoMes||0),0),[kpi]);
  const totDia = useMemo(()=>kpi.filter(k=>k.modulo!=='TOTAL CONSOLIDADO').reduce((s,k)=>s+Number(k.captacionDia||0),0),[kpi]);
  const totComprob = useMemo(()=>kpi.filter(k=>k.modulo!=='TOTAL CONSOLIDADO').reduce((s,k)=>s+Number(k.cantidadComprobantes||0),0),[kpi]);
  const totalPendiente = useMemo(()=>pendientes.reduce((s,p)=>s+(Number(p.total??p.TOTAL)||0),0),[pendientes]);


  const filasComprobantes = useMemo(() => {
    const sum = (arr,key) => arr.reduce((s,x)=>s+Number(x[key]||0),0);
    return ['BOLETA','FACTURA','NOTA DE CREDITO','NOTA DE DEBITO'].map(tipo => {
      const afecta = comprobantes.filter(c=>String(c.tipoComprobante||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toUpperCase()===tipo && c.modulo!=='INAFECTOS - CAJA');
      const inafecta = comprobantes.filter(c=>c.tipoComprobante===tipo && c.modulo==='INAFECTOS - CAJA');
      return {
        tipo,
        cantAfecta: sum(afecta,'cantidad'), montoAfecta: sum(afecta,'monto'),
        cantInafecta: sum(inafecta,'cantidad'), montoInafecta: sum(inafecta,'monto'),
      };
    });
  }, [comprobantes]);
  const totCantAfecta = filasComprobantes.reduce((s,f)=>s+f.cantAfecta,0);
  const totMontoAfecta = filasComprobantes.reduce((s,f)=>s+f.montoAfecta,0);
  const totCantInafecta = filasComprobantes.reduce((s,f)=>s+f.cantInafecta,0);
  const totMontoInafecta = filasComprobantes.reduce((s,f)=>s+f.montoInafecta,0);

  // ── Datos de tendencia pivotados por fecha ──────────────────────────────
  const tendenciaPivot = useMemo(() => {
    const fechas = [...new Set(tendencia.map(t=>t.fecha))].sort();
    return fechas.map(f => {
      const row = { fecha: f };
      MOD_ORDER.forEach(m => {
        const item = tendencia.find(t=>t.fecha===f && t.modulo===m);
        row[m] = item ? Number(item.monto||0) : 0;
      });
      return row;
    });
  }, [tendencia]);



  const localDay=d=>new Date(d.getTime()-5*3600000).toISOString().slice(0,10);
  const filaHoy=tendenciaPivot.find(r=>String(r.fecha).slice(0,10)===localDay(new Date()));
  const filaAyer=tendenciaPivot.find(r=>String(r.fecha).slice(0,10)===localDay(new Date(Date.now()-86400000)));
  const totalHoyTend = filaHoy ? MOD_ORDER.reduce((s,m)=>s+Number(filaHoy[m]||0),0) : 0;
  const totalAyerTend = filaAyer ? MOD_ORDER.reduce((s,m)=>s+Number(filaAyer[m]||0),0) : 0;
  const deltaHoyPct = filaHoy&&filaAyer&&totalAyerTend>0 ? ((totalHoyTend-totalAyerTend)/totalAyerTend*100) : null;

  const chartDonut = useMemo(() => {
    const filas = kpi.filter(k=>k.modulo!=='TOTAL CONSOLIDADO' && MOD_ORDER.includes(k.modulo));
    return {
      labels: filas.map(f=>MOD_COLOR[f.modulo]?.label||f.modulo),
      datasets: [{ data: filas.map(f=>Number(f.totalRecaudadoMes||0)), backgroundColor: filas.map(f=>MOD_COLOR[f.modulo]?.color||IOS.blue), borderWidth:0, hoverOffset:6 }]
    };
  }, [kpi]);

  const toggleModulo = (m) => setModulosVisibles(prev => {
    const next = new Set(prev);
    if (next.has(m) && next.size>1) next.delete(m); else next.add(m);
    return next;
  });

  const dias = diasRestantes(cert?.fechA_FIN_CERT);
  const certColor = dias===null?IOS.label2:dias<=15?IOS.red:dias<=60?IOS.orange:IOS.green;

  return (
    <Box className="board-page" sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
      <HeaderIOS titulo="Tablero de Control" subtitulo="Economía"
        onHome={()=>navigate(`/${modulo}`)} onLogout={handleLogout}
        nombreCompleto={nc} avatarLetra={nc.charAt(0).toUpperCase()} />

      <Box sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

        <div className="board-intro"><span>Gestión · Economía</span><h1>Tu recaudación, en perspectiva</h1><p>Revisa los resultados y el estado de los comprobantes de tu ejecutora.</p></div>
        {Object.entries(errors).filter(([,message])=>message).map(([key,message])=><Alert key={key} severity="warning" sx={{mb:2}} action={<Button onClick={()=>{cargarKpi();cargarComprobantes();cargarTendencia();setSecondaryRefresh(n=>n+1);}}>Reintentar</Button>}>{message}</Alert>)}
        {/* ══ HERO ══════════════════════════════════════════════════════ */}
        <Box sx={{ position:'relative', borderRadius:{ xs:'22px', sm:'28px' }, overflow:'hidden', mb:2.5,
          background:'linear-gradient(135deg, #3277AD 0%, #397FB2 100%)',
          boxShadow:'0 6px 24px rgba(32,52,78,0.045)', p:{ xs:2.2, sm:4 } }}>

          {/* decoraciones */}
          <Box sx={{ position:'absolute', top:-60, right:-40, width:220, height:220, borderRadius:'50%', bgcolor:'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position:'absolute', bottom:-80, left:-30, width:180, height:180, borderRadius:'50%', bgcolor:'rgba(255,255,255,0.06)' }} />

          <Box sx={{ position:'relative' }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:{ xs:1.5, sm:0 } }}>
              <Box sx={{ minWidth:0 }}>
                <Typography sx={{ color:'rgba(255,255,255,0.65)', fontSize:{ xs:'0.7rem', sm:'0.8rem' }, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Recaudación · {periodoActual.label || `${periodoActual.mes}/${periodoActual.periodo}`}</Typography>
              </Box>
              <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.6, flexShrink:0 }}>
                <Box component="button" aria-label="Actualizar tablero" disabled={loadKpi||loadTend||loadComp||cargSec} onClick={()=>{cargarKpi();cargarTendencia();cargarComprobantes();setSecondaryRefresh(n=>n+1);}}
                  sx={{ width:34, height:34, borderRadius:'50%', border:'none', bgcolor:'rgba(255,255,255,0.18)', cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)', transition:'all 0.15s',
                    '&:hover':{ bgcolor:'rgba(255,255,255,0.28)' } }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                </Box>
                {ultimaAct && <Typography sx={{ color:'rgba(255,255,255,0.55)', fontSize:'0.58rem', whiteSpace:'nowrap' }}>{ultimaAct.toLocaleTimeString('es-PE',{hour:'2-digit',minute:'2-digit'})}</Typography>}
              </Box>
            </Box>

            <Typography sx={{ color:'white', fontSize:{ xs:'2.1rem', sm:'3.2rem' }, fontWeight:900, lineHeight:1, letterSpacing:'-0.03em', textShadow:'0 4px 20px rgba(0,0,0,0.15)', mb:{ xs:1.5, sm:2 } }}>
              {loadKpi ? <Box sx={{ ...shimmerSx, height:{xs:32,sm:50}, width:{xs:170,sm:220}, borderRadius:'12px', bgcolor:'rgba(255,255,255,0.25)' }} /> : errors.kpi ? '—' : <AnimatedNumber value={totMes} decimals={2} prefix="S/ " />}
            </Typography>

            <Box sx={{ display:'flex', gap:{ xs:2.5, sm:4 }, flexWrap:'wrap' }}>
              <Box>
                <Typography sx={{ color:'rgba(255,255,255,0.6)', fontSize:'0.58rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>Comprobantes del Mes</Typography>
                <Typography sx={{ color:'white', fontSize:{ xs:'0.98rem', sm:'1.2rem' }, fontWeight:800 }}>{loadKpi||errors.kpi?'—':totComprob.toLocaleString()}</Typography>
              </Box>
              {totalPendiente>0 && (
                <>
                  <Box sx={{ width:'1px', bgcolor:'rgba(255,255,255,0.25)' }} />
                  <Box>
                    <Typography sx={{ color:'rgba(255,255,255,0.6)', fontSize:'0.58rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>Pendientes de Envío a SUNAT</Typography>
                    <Typography sx={{ color:'#FFD60A', fontSize:{ xs:'0.98rem', sm:'1.2rem' }, fontWeight:800, lineHeight:1.2 }}>{totalPendiente} CPE</Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {esMesActual&&<>
        {/* ══ HOY EN UN VISTAZO ═════════════════════════════════════════ */}
        <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:2, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, mb:2.5 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:0.8, mb:1.8 }}>
            <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.green, flexShrink:0,
              boxShadow:`0 0 0 4px ${IOS.green}18`, animation:'pulseHoy 1.6s ease-in-out infinite',
              '@keyframes pulseHoy':{ '0%,100%':{opacity:1}, '50%':{opacity:0.4} } }} />
            <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>{esMesActual?'Hoy en un vistazo':'Actividad diaria · consulta el mes actual'}</Typography>
          </Box>

          <Box sx={{ display:'flex', flexWrap:'wrap', gap:{ xs:2.5, sm:4 }, alignItems:'flex-end', mb:2 }}>
            <Box>
              <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.06em', mb:0.3 }}>Recaudado hoy</Typography>
              <Typography sx={{ fontSize:{ xs:'1.7rem', sm:'2.1rem' }, fontWeight:800, color:'#374151', lineHeight:1, letterSpacing:'-0.02em' }}>
                {loadKpi ? '—' : <AnimatedNumber value={totDia} decimals={2} prefix="S/ " />}
              </Typography>
              {deltaHoyPct!==null && !loadTend && (
                <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.4, mt:0.6 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={deltaHoyPct>=0?IOS.green:IOS.red} strokeWidth="2.6" strokeLinecap="round">
                    {deltaHoyPct>=0 ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                  </svg>
                  <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:deltaHoyPct>=0?IOS.green:IOS.red }}>{deltaHoyPct>=0?'+':''}{deltaHoyPct.toFixed(0)}%</Typography>
                  <Typography sx={{ fontSize:'0.68rem', color:IOS.label2 }}>vs ayer</Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.06em', mb:0.3 }}>Comprobantes hoy</Typography>
              <Typography sx={{ fontSize:{ xs:'1.4rem', sm:'1.7rem' }, fontWeight:800, color:'#374151', lineHeight:1 }}>
                {loadKpi ? '—' : kpi.filter(k=>MOD_ORDER.includes(k.modulo)).reduce((s,k)=>s+Number(k.cantidadDia||0),0)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.06em', mb:0.3 }}>Aperturas activas</Typography>
              <Typography sx={{ fontSize:{ xs:'1.4rem', sm:'1.7rem' }, fontWeight:800, color:'#374151', lineHeight:1 }}>
                {loadKpi ? '—' : kpi.filter(k=>MOD_ORDER.includes(k.modulo)).reduce((s,k)=>s+Number(k.aperturasActivasHoy||0),0)}
              </Typography>
            </Box>
          </Box>

          {/* Barra de composición del día por módulo */}
          {!loadKpi && (
            <>
              <Box sx={{ display:'flex', height:10, borderRadius:'6px', overflow:'hidden', mb:1 }}>
                {MOD_ORDER.map(m=>{
                  const cfg = MOD_COLOR[m];
                  const val = Number(kpi.find(k=>k.modulo===m)?.captacionDia||0);
                  const pct = totDia>0 ? (val/totDia*100) : 0;
                  return pct>0 ? <Box key={m} sx={{ width:`${pct}%`, bgcolor:cfg.color, transition:'width 0.6s ease' }} /> : null;
                })}
                {totDia===0 && <Box sx={{ width:'100%', bgcolor:IOS.bg }} />}
              </Box>
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.7 }}>
                {MOD_ORDER.map(m=>{
                  const cfg = MOD_COLOR[m];
                  const item = kpi.find(k=>k.modulo===m);
                  const val = Number(item?.captacionDia||0);
                  const cant = Number(item?.cantidadDia||0);
                  const pct = totDia>0 ? (val/totDia*100) : 0;
                  return (
                    <Box key={m} sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                      <Box sx={{ width:7, height:7, borderRadius:'2px', bgcolor:cfg.color, flexShrink:0 }} />
                      <Typography sx={{ fontSize:'0.7rem', color:IOS.label, fontWeight:600, flex:1, minWidth:0 }}>{cfg.label}</Typography>
                      <Typography sx={{ fontSize:'0.68rem', color:IOS.label2, flexShrink:0 }}>{cant} comprob.</Typography>
                      <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:'#374151', flexShrink:0, minWidth:70, textAlign:'right' }}>{fmtS(val)}</Typography>
                      <Typography sx={{ fontSize:'0.66rem', fontWeight:700, color:cfg.color, flexShrink:0, minWidth:32, textAlign:'right' }}>{pct.toFixed(0)}%</Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>

        </>}
        {/* ══ MÓDULOS ═══════════════════════════════════════════════════ */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:1, mb:1.2 }}>
          <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase' }}>
            {esMesActual ? 'Mes en curso' : `Corte histórico · ${periodoActual.label}`}
          </Typography>
          <Box sx={{ display:'flex', gap:0.4, bgcolor:IOS.surface, borderRadius:'10px', p:0.4, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, overflowX:'auto', maxWidth:'100%' }}>
            {periodos.map((p,i)=>(
              <Box key={i} onClick={()=>setPeriodoIdx(i)}
                sx={{ px:{ xs:0.85, sm:1.1 }, py:0.5, borderRadius:'8px', cursor:'pointer', flexShrink:0,
                  bgcolor:periodoIdx===i?IOS.blue:'transparent', transition:'all 0.15s' }}>
                <Typography sx={{ fontSize:{ xs:'0.62rem', sm:'0.68rem' }, fontWeight:periodoIdx===i?700:500, color:periodoIdx===i?'white':IOS.label2, whiteSpace:'nowrap', textTransform:'capitalize' }}>{p.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'repeat(3,1fr)' }, gap:2, mb:2.5 }}>
          {MOD_ORDER.map(m => (
            <ModuloCard key={m} modulo={m} loading={loadKpi} data={kpi.find(k=>k.modulo===m)} esMesActual={esMesActual} />
          ))}
        </Box>

        {/* ══ TENDENCIA + DONUT ═════════════════════════════════════════ */}
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', lg:'1.7fr 1fr' }, gap:2, mb:2.5, alignItems:'stretch' }}>

          {/* Tendencia */}
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:1.8, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}` }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.5 }}>
              <Box sx={{ width:34, height:34, borderRadius:'10px', bgcolor:`${IOS.blue}14`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.1-2.8-2.8L6 15.1"/></svg>
              </Box>
              <Box sx={{ minWidth:0 }}>
                <Typography sx={{ fontWeight:700, fontSize:'0.85rem', color:IOS.label }}>Tendencia de Recaudación</Typography>
                <Typography sx={{ fontSize:'0.65rem', color:IOS.label2 }}>Últimos {rangoDias} días · independiente del mes seleccionado</Typography>
              </Box>
            </Box>
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
              <Box sx={{ display:'flex', gap:0.3, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
                {[7,15,30,90].map(d=>(
                  <Box component="button" key={d} aria-pressed={rangoDias===d} onClick={()=>setRangoDias(d)}
                    sx={{ px:1.1, py:0.45, borderRadius:'8px', cursor:'pointer', bgcolor:rangoDias===d?IOS.surface:'transparent',
                      boxShadow:rangoDias===d?'0 1px 3px rgba(0,0,0,0.1)':'none', transition:'all 0.15s' }}>
                    <Typography sx={{ fontSize:'0.65rem', fontWeight:rangoDias===d?700:500, color:rangoDias===d?IOS.blue:IOS.label2 }}>{d}d</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display:'flex', gap:0.3, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
                {[['area','Área'],['barras','Barras']].map(([v,l])=>(
                  <Box component="button" key={v} aria-pressed={tipoChart===v} onClick={()=>setTipoChart(v)}
                    sx={{ px:1.1, py:0.45, borderRadius:'8px', cursor:'pointer', bgcolor:tipoChart===v?IOS.surface:'transparent',
                      boxShadow:tipoChart===v?'0 1px 3px rgba(0,0,0,0.1)':'none', transition:'all 0.15s' }}>
                    <Typography sx={{ fontSize:'0.65rem', fontWeight:tipoChart===v?700:500, color:tipoChart===v?IOS.blue:IOS.label2 }}>{l}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Chips de módulo */}
            <Box sx={{ display:'flex', gap:0.8, flexWrap:'wrap', mb:2 }}>
              {MOD_ORDER.map(m=>{
                const cfg = MOD_COLOR[m];
                const activo = modulosVisibles.has(m);
                return (
                  <Box component="button" key={m} aria-pressed={activo} onClick={()=>toggleModulo(m)}
                    sx={{ display:'flex', alignItems:'center', gap:0.5, px:{xs:0.9,sm:1.1}, py:0.4, borderRadius:'24px', cursor:'pointer',
                      bgcolor:activo?`${cfg.color}14`:IOS.bg, border:`1px solid ${activo?cfg.color:IOS.separator}`, opacity:activo?1:0.55, transition:'all 0.15s' }}>
                    <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:cfg.color, flexShrink:0 }} />
                    <Typography sx={{ fontSize:{xs:'0.62rem',sm:'0.68rem'}, fontWeight:700, color:activo?cfg.color:IOS.label2 }}>{cfg.label}</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ height:{ xs:220, sm:260 } }}>
              {loadTend
                ? <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                : tendenciaPivot.length===0
                  ? <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos en el rango</Typography></Box>
                  : <BoardTrend rows={tendenciaPivot} modules={MOD_ORDER.filter(m=>modulosVisibles.has(m))} colors={MOD_COLOR} type={tipoChart}/>
              }
            </Box>
          </Box>

          {/* Donut distribución */}
          <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:1.8, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, display:'flex', flexDirection:'column' }}>
            <Typography sx={{ fontWeight:700, fontSize:{ xs:'0.82rem', sm:'0.88rem' }, color:IOS.label, mb:0.2 }}>Distribución del Mes</Typography>
            <Typography sx={{ fontSize:'0.65rem', color:IOS.label2, mb:{ xs:1.5, sm:2 } }}>Participación por módulo</Typography>
            {loadKpi
              ? <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', minHeight:160 }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
              : totMes===0
                ? <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', minHeight:160 }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography></Box>
                : <>
                    <Box sx={{ height:{ xs:210, sm:240 }, maxWidth:320, width:'100%', mx:'auto', position:'relative', mb:2.5 }}>
                      <BoardRing data={chartDonut}/>
                      <Box sx={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center' }}>
                        <Typography sx={{ fontSize:'0.6rem', color:IOS.label2, fontWeight:700, textTransform:'uppercase' }}>Total</Typography>
                        <Typography sx={{ fontSize:{ xs:'0.95rem', sm:'1.05rem' }, fontWeight:800, color:IOS.label }}>{fmtS(totMes)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
                      {MOD_ORDER.map(m=>{
                        const item = kpi.find(k=>k.modulo===m);
                        const cfg = MOD_COLOR[m];
                        const monto = Number(item?.totalRecaudadoMes||0);
                        const pct = totMes>0 ? (monto/totMes*100) : 0;
                        return (
                          <Box key={m} sx={{ display:'flex', alignItems:'center', gap:1, py:0.4 }}>
                            <Box sx={{ width:9, height:9, borderRadius:'2px', bgcolor:cfg.color, flexShrink:0 }} />
                            <Typography sx={{ fontSize:{ xs:'0.72rem', sm:'0.76rem' }, fontWeight:600, color:IOS.label, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cfg.label}</Typography>
                            <Typography sx={{ fontSize:{ xs:'0.7rem', sm:'0.74rem' }, color:IOS.label2, flexShrink:0, mr:1 }}>{fmtS(monto)}</Typography>
                            <Box sx={{ px:0.8, py:0.2, borderRadius:'24px', bgcolor:`${cfg.color}14`, flexShrink:0 }}>
                              <Typography sx={{ fontSize:{ xs:'0.68rem', sm:'0.72rem' }, fontWeight:800, color:cfg.color }}>{pct.toFixed(0)}%</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
            }
          </Box>
        </Box>

        {/* ══ COMPROBANTES DEL MES ══════════════════════════════════════ */}
        <Box sx={{ bgcolor:IOS.surface, borderRadius:'24px', p:{ xs:2, sm:2.5 }, boxShadow:'0 6px 24px rgba(32,52,78,0.045)', border:`1px solid ${IOS.separator}`, mb:2.5 }}>
          <Typography sx={{ fontWeight:700, fontSize:{ xs:'0.85rem', sm:'0.88rem' }, color:IOS.label, mb:0.2 }}>Comprobantes del Mes</Typography>
          <Typography sx={{ fontSize:'0.65rem', color:IOS.label2, mb:2 }}>Por tipo de documento — {esMesActual?'mes en curso':periodoActual.label}</Typography>

          {loadComp
            ? <Box sx={{ py:4, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
            : <Box sx={{ overflowX:'auto' }}>
                <table style={{ width:'100%', minWidth:520, borderCollapse:'collapse', fontSize:'0.78rem' }}>
                  <thead>
                    <tr>
                      <th style={{ padding:'8px 10px', textAlign:'left', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Documento</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Afectas (cant.)</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Afectas (S/)</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Inafectas (cant.)</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Inafectas (S/)</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Total (cant.)</th>
                      <th style={{ padding:'8px 10px', textAlign:'right', fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase' }}>Total (S/)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasComprobantes.map(f=>{
                      const esNC = f.tipo==='NOTA DE CREDITO';
                      const cantTot = f.cantAfecta+f.cantInafecta, montoTot = f.montoAfecta+f.montoInafecta;
                      return (
                        <tr key={f.tipo} style={{ borderBottom:`1px solid ${IOS.separator}` }}>
                          <td style={{ padding:'9px 10px', fontWeight:700, color:'#1F2937' }}>{f.tipo==='NOTA DE CREDITO'?'Notas de Crédito':f.tipo==='NOTA DE DEBITO'?'Notas de Débito':f.tipo.charAt(0)+f.tipo.slice(1).toLowerCase()+'s'}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', color:'#374151' }}>{f.cantAfecta}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', color:esNC?IOS.red:'#374151' }}>{fmtS(f.montoAfecta)}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', color:'#374151' }}>{f.cantInafecta}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', color:esNC?IOS.red:'#374151' }}>{fmtS(f.montoInafecta)}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:'#1F2937' }}>{cantTot}</td>
                          <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:esNC?IOS.red:'#1F2937' }}>{fmtS(montoTot)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor:IOS.bg }}>
                      <td style={{ padding:'9px 10px', fontWeight:800, color:IOS.label, fontSize:'0.72rem', textTransform:'uppercase' }}>Total</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.label }}>{totCantAfecta}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.label }}>{fmtS(totMontoAfecta)}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.label }}>{totCantInafecta}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.label }}>{fmtS(totMontoInafecta)}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.blue, fontSize:'0.85rem' }}>{totCantAfecta+totCantInafecta}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.blue, fontSize:'0.85rem' }}>{fmtS(totMontoAfecta+totMontoInafecta)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Box>
          }
        </Box>

        {/* ══ SECUNDARIAS: Certificado / SUNAT / Notas Crédito ═══════════ */}
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'repeat(3,1fr)' }, gap:2 }}>

          {/* Certificado Digital */}
          <SecCard>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.8 }}>
              <Box sx={{ width:38, height:38, borderRadius:'12px', bgcolor:`${certColor}14`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={certColor} strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="8" r="5"/><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.11"/></svg>
              </Box>
              <Box sx={{ minWidth:0 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.07em' }}>Certificado Digital</Typography>
                {cargSec ? <Box sx={{...shimmerSx, height:14, width:100, borderRadius:'4px', bgcolor:IOS.bg, mt:0.3}}/> : <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:IOS.label, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cert?.razoN_SOCIAL||'Sin configurar'}</Typography>}
              </Box>
            </Box>
            {!cargSec && cert && (
              <>
                <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.6, px:1, py:0.3, borderRadius:'24px', bgcolor:`${certColor}14`, border:`1px solid ${certColor}30`, mb:1.5 }}>
                  <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:certColor }} />
                  <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:certColor }}>{dias===null?'Sin datos':dias<=0?'VENCIDO':`Vence en ${dias}d`}</Typography>
                </Box>
                <Typography sx={{ fontSize:'0.7rem', color:IOS.label2 }}>Vigente hasta {fmtFecha(cert?.fechA_FIN_CERT)}</Typography>
              </>
            )}
          </SecCard>

          {/* SUNAT Pendientes */}
          <SecCard>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.8 }}>
              <Box sx={{ width:38, height:38, borderRadius:'12px', bgcolor:`${pendientes.length>0?IOS.red:IOS.green}14`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={pendientes.length>0?IOS.red:IOS.green} strokeWidth="1.7" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </Box>
              <Box sx={{ minWidth:0 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.07em' }}>CPE Pendientes SUNAT</Typography>
                {cargSec ? <Box sx={{...shimmerSx, height:22, width:60, borderRadius:'4px', bgcolor:IOS.bg, mt:0.3}}/> : (
                  <Typography sx={{ fontSize:'1.3rem', fontWeight:800, color:totalPendiente>0?IOS.red:IOS.green, lineHeight:1 }}>{totalPendiente}</Typography>
                )}
              </Box>
            </Box>
            {!cargSec && (
              totalPendiente>0
                ? <Typography sx={{ fontSize:'0.75rem', color:IOS.label2 }}>en {pendientes.length} establecimiento{pendientes.length!==1?'s':''}</Typography>
                : <Typography sx={{ fontSize:'0.75rem', color:'#1A6B3C', fontWeight:600 }}>Todo enviado a SUNAT</Typography>
            )}
            <Box component="button" onClick={()=>navigate('/economia/monitor-envios')}
              sx={{ width:'100%', mt:1.5, py:0.8, borderRadius:'10px', border:`1px solid ${IOS.separator}`, bgcolor:IOS.bg, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:0.6, transition:'all 0.15s', '&:hover':{ bgcolor:`${IOS.blue}08` } }}>
              <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.blue }}>Ver Monitor de Envíos →</Typography>
            </Box>
          </SecCard>

          {/* Notas de Crédito Automáticas */}
          <SecCard>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.8 }}>
              <Box sx={{ width:38, height:38, borderRadius:'12px', bgcolor:`${IOS.purple}14`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </Box>
              <Box sx={{ minWidth:0 }}>
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.07em' }}>Notas de Crédito Auto.</Typography>
                {cargSec ? <Box sx={{...shimmerSx, height:14, width:70, borderRadius:'4px', bgcolor:IOS.bg, mt:0.3}}/> : <Typography sx={{ fontSize:'0.85rem', fontWeight:700, color:IOS.label }}>{horaNC||'No configurado'}</Typography>}
              </Box>
            </Box>
            {!cargSec && logNC.length>0 && (()=>{
              const ult = logNC[logNC.length-1];
              return (
                <Box sx={{ p:1.2, bgcolor:IOS.bg, borderRadius:'10px' }}>
                  <Typography sx={{ fontSize:'0.68rem', color:IOS.label2, mb:0.4 }}>Último envío: {(ult.fechaEjecucion||'').replace('T',' ').slice(0,16)}</Typography>
                  <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.purple }}>{ult.registrosInsertados} notas · {ult.seriesAfectadas} series</Typography>
                </Box>
              );
            })()}
            {!cargSec && logNC.length===0 && <Typography sx={{ fontSize:'0.75rem', color:IOS.label2 }}>Sin registros aún</Typography>}
          </SecCard>
        </Box>
      </Box>
    </Box>
  );
};

export default TableroEconomia;